import { useEffect, useMemo, useState } from "react"
import useSources from "~/ui-rtk/hooks/sources"
import { AggregationType, VISUALIZATIONS_MAP } from "~/ui-rtk/constants/charts"
import {
  type TBrandMediaOptimizationTableId,
  type TBrandMediaOptimizationTable,
} from "~/ui-rtk/pages/BrandMedia/PaidMediaWeights/connect"
import { BRAND_HEALTH_OVERVIEW_SOURCES_TO_BRAND_MEDIA_OPTIMIZATIONS_SOURCES_MAP } from "~/ui-rtk/constants/brand-media"
import type { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import type {
  TableVisualizationColumn,
  TColumnFiltersState,
} from "~/ui-rtk/constants/table-visualization-types"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { TCubeFilterOptions } from "~/ui-rtk/shared/types/charts"
import { type Query as TCubeQuery } from "@cubejs-client/core"
import {
  SOURCE_ID_TO_MEDIA_CHANNEL_MAPPING,
  TBrandMediaDashboardCharts,
  URL_PARAM_TO_WIDGET_MAP,
  VISUALIZATION_TABLE,
} from "./constants"
import {
  URL_ADSET_ID_PARAM_KEY,
  URL_CAMPAIGN_ID_PARAM_KEY,
  URL_WIDGET_PARAM_KEY,
} from "~/ui-rtk/constants/url-params"
import { usePeriod, DATE_FORMAT } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"
import { useLastUpdateDate, useCubeQuery } from "~/ui-rtk/hooks/cube"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import useUrlColumnFilters from "~/ui-rtk/hooks/url-column-filters"
import useUrlColumnKeys from "~/ui-rtk/hooks/url-column-keys"
import useUrlSourceKeys from "~/ui-rtk/hooks/url-source-keys"
import useDepVar from "~/ui-rtk/hooks/dep-var"
import {
  convertToCubeFilters,
  polyfillCubeQueryWithTimeDimension,
} from "~/ui-rtk/utils/cube"
import MarathonDataLogo from "~/ui-rtk/components/ui/svg/Logo"
import useMediaOptimizationMediaChannels from "~/ui-rtk/hooks/media-optimization-media-channel"
import { capitalize } from "~/ui-rtk/utils/format"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import type { RowSelectionState } from "@tanstack/react-table"
import useRevenueFromSearch from "~/ui-rtk/hooks/revenue-from-search"
import { useAutoClassify } from "./hooks/useAutoClassify"
import {
  InitiativeTypeContainer,
  InitiativeTypeDropdown,
  useInitiativeTypeManagement,
  buildAdsetIdsByCampaign,
  getRowChildAdsetIds,
} from "~/ui-rtk/components/features/initiative-type"

// Import containers
import { TabContainer, ControlsContainer, ContentContainer } from "./containers"

export interface TBrandMediaDashboardProps {
  title: string
  slug: string
  periodPicker?: { visible: boolean }
  items: TBrandMediaDashboardCharts
  parent: {
    slug: string
    title: string
  }
}

const dayjs = useDate()
const maxDateRestriction = dayjs().toDate()
const BRAND_MEDIA_ID = "brand_media"

// Content component that uses the hook
function PaidMediaWeightingContent({
  parent,
  slug,
  title,
  items,
}: TBrandMediaDashboardProps) {
  const { params, setParams } = useURLSearchParams()
  const { sources } = useSources()
  const { setPeriod } = usePeriod()
  const currentCompany = useCurrentCompany()
  const [highlight, setHighlight] = useState(true)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const isRevenueFromSearchEnabled = useRevenueFromSearch()

  const { amazonEnabled, toggleAmazon, ...depVarConfig } = useDepVar()
  const {
    classify: autoClassify,
    isClassifying,
    hasUnassignedAdsets,
  } = useAutoClassify()

  // Use initiative type management hook - now called within the container context
  const {
    getCurrentInitiativeType,
    getInitiativeTypeOptions,
    handleInitiativeTypeChange,
  } = useInitiativeTypeManagement()

  const visibleWidgets: TBrandMediaDashboardCharts = useMemo(
    (): TBrandMediaDashboardCharts =>
      Object.keys(items)
        .filter(chartId => {
          const { requiredSources } =
            items[chartId as unknown as TBrandMediaOptimizationTableId]
          return (
            !requiredSources?.length ||
            requiredSources?.some((source: string) => sources?.has(source))
          )
        })
        .reduce(
          (
            filteredCharts: Record<
              TBrandMediaOptimizationTableId,
              TBrandMediaOptimizationTable
            >,
            chartId: string,
          ) => {
            filteredCharts[chartId as TBrandMediaOptimizationTableId] =
              items[chartId as unknown as TBrandMediaOptimizationTableId]
            return filteredCharts
          },
          {} as Record<
            TBrandMediaOptimizationTableId,
            TBrandMediaOptimizationTable
          >,
        ),
    [items, sources],
  )

  const defaultVisibleChart = useMemo(
    () =>
      visibleWidgets[
        Object.keys(
          visibleWidgets,
        )[0] as unknown as TBrandMediaOptimizationTableId
      ],
    [visibleWidgets],
  )

  const widgetURLSearchParam =
    params[URL_WIDGET_PARAM_KEY] ?? defaultVisibleChart?.id

  const activeWidget = useMemo(
    () =>
      widgetURLSearchParam && URL_PARAM_TO_WIDGET_MAP[widgetURLSearchParam]
        ? visibleWidgets[
            widgetURLSearchParam.toUpperCase() as TBrandMediaOptimizationTableId
          ]
        : defaultVisibleChart,
    [widgetURLSearchParam, visibleWidgets, defaultVisibleChart],
  )

  const { dateMetric, filterMetrics } = activeWidget?.lastDateChipProps ?? {}

  const { lastDate, isLoading: isLastUpdateDateLoading } = useLastUpdateDate({
    dateMetric,
    filterMetrics,
  })

  const { dateRange } = useMinMaxDateRange(
    undefined,
    lastDate ? new Date(lastDate) : maxDateRestriction,
    AggregationType.DAILY,
    true,
  )

  const lastDateRestriction = useMemo(
    () => (lastDate ? new Date(lastDate) : maxDateRestriction),
    [lastDate, maxDateRestriction],
  )

  const chartProps = useMemo(
    () => VISUALIZATIONS_MAP[activeWidget?.widget]?.props,
    [activeWidget],
  )

  const queryPrefix = useMemo(() => chartProps?.queryPrefix, [chartProps])

  const defaultColumnKeys = useMemo(() => {
    const props = VISUALIZATIONS_MAP[activeWidget?.widget]?.props
    if (!isRevenueFromSearchEnabled) {
      const revenueFromSearchColumns = [
        "revenue_from_search",
        "revenue_from_search_roas",
      ]
      return props?.columnConfig
        .filter(
          (column: TableVisualizationColumn) =>
            !revenueFromSearchColumns.includes(column.key),
        )
        .map((column: TableVisualizationColumn) => column.key)
    }
    return props?.columnConfig.reduce(
      (
        acc: string[],
        column: TableVisualizationColumn & { disabled: boolean },
      ) => {
        if (!column.disabled) {
          acc.push(column.key)
        }
        return acc
      },
      [],
    )
  }, [activeWidget, isRevenueFromSearchEnabled])

  const querySortBy = params.sort_by
  const sortBy = useMemo(
    () =>
      querySortBy
        ? `${queryPrefix}.${querySortBy}`
        : `${queryPrefix}.${chartProps?.sortBy}`,
    [querySortBy, queryPrefix, chartProps],
  )

  const chartPropsSorting = useMemo(
    () =>
      sortBy
        ? [
            {
              id: sortBy,
              desc: true,
            },
          ]
        : [],
    [sortBy],
  )

  const { columnFilters, setColumnFilters } =
    useUrlColumnFilters(BRAND_MEDIA_ID)
  const { columnKeys, setColumnKeys } = useUrlColumnKeys(
    BRAND_MEDIA_ID,
    defaultColumnKeys,
  )

  const columnOptions = useMemo(() => {
    const options = chartProps?.columnConfig.map(
      (column: TableVisualizationColumn) =>
        ({
          value: column.key,
          label: column.header,
          icon: column.isBrand ? <MarathonDataLogo /> : null,
        }) as TMultiSelectOption,
    )

    return options || []
  }, [chartProps])

  const {
    request,
    isLoading: isMediaChannelsLoading,
    mediaChannels,
  } = useMediaOptimizationMediaChannels(queryPrefix)

  const querySelectedSource = params.selected_sources
  const initialSources = useMemo(() => {
    if (querySelectedSource) {
      const brandMediaSources = querySelectedSource
        .split(",")
        .map(
          (sourceId: string) =>
            BRAND_HEALTH_OVERVIEW_SOURCES_TO_BRAND_MEDIA_OPTIMIZATIONS_SOURCES_MAP[
              sourceId
            ],
        )
      return [...new Set(brandMediaSources)].filter(source =>
        activeWidget?.requiredSources?.includes(source),
      )
    }
    return mediaChannels || []
  }, [querySelectedSource, activeWidget, mediaChannels])

  const { sourceKeys, setSourceKeys } = useUrlSourceKeys(
    BRAND_MEDIA_ID,
    initialSources,
  )

  const sourcesOptions = useMemo(() => {
    if (
      !activeWidget?.requiredSources ||
      !mediaChannels ||
      isMediaChannelsLoading
    ) {
      return []
    }

    const options = mediaChannels.map(channel => ({
      value: channel,
      label: channel.split("_").map(capitalize).join(" "),
      isBeta: Array.from(sources.values()).find(
        source => source.id === channel.toUpperCase(),
      )?.isBeta,
    }))

    return options
  }, [activeWidget, sources, mediaChannels, isMediaChannelsLoading])

  const cubeFilters = useMemo(() => {
    const filters: Record<string, string | string[] | undefined | null> = {
      campaign_id: [VISUALIZATION_TABLE.AD_SET].includes(activeWidget?.widget)
        ? params[URL_CAMPAIGN_ID_PARAM_KEY]
        : undefined,
      adset_id: undefined,
      dep_var: depVarConfig.depVar,
      dep_var_scaler: depVarConfig.depVarScaler,
    }

    const baseQuery = convertToCubeFilters(queryPrefix, filters)

    return {
      and: [
        ...baseQuery,
        {
          or: sourceKeys.map((sourceId: string) => ({
            member: `${queryPrefix}.media_channel`,
            operator: "equals",
            values: [SOURCE_ID_TO_MEDIA_CHANNEL_MAPPING[sourceId] ?? sourceId],
          })),
        },
      ],
    } as TCubeFilterOptions
  }, [sourceKeys, queryPrefix, params, activeWidget, depVarConfig])

  // Adset query for sharing data with child components (Revenue Breakdown and Initiative Type Modal)
  const adsetRevenueQuery: TCubeQuery = useMemo(() => {
    if (!currentCompany?.id) {
      return null as unknown as TCubeQuery
    }

    const queryPrefix = "company_media_metrics_adsets"

    // Use last 30 days for objective/goal spending data
    const endDate = new Date()
    const startDate = new Date(endDate)
    startDate.setDate(startDate.getDate() - 30)

    const baseQuery: TCubeQuery = {
      measures: [
        `${queryPrefix}.purchases`,
        `${queryPrefix}.spend`,
        `${queryPrefix}.modeled_revenue`,
        `${queryPrefix}.brand_value_agg`,
        `${queryPrefix}.purchase_value_1d_view`,
        `${queryPrefix}.purchase_value_1d_engaged_view`,
        `${queryPrefix}.ga4_total_revenue`,
        `${queryPrefix}.revenue_from_search`,
        `${queryPrefix}.shopify_first_touch_revenue`,
        `${queryPrefix}.shopify_last_touch_revenue`,
        `${queryPrefix}.triple_whale_linear_total_revenue`,
        `${queryPrefix}.triple_whale_last_platform_click_total_revenue`,
        `${queryPrefix}.triple_whale_full_first_click_total_revenue`,
        `${queryPrefix}.triple_whale_full_last_click_total_revenue`,
        `${queryPrefix}.triple_whale_linear_all_total_revenue`,
        `${queryPrefix}.triple_whale_linear_nc_total_revenue`,
        `${queryPrefix}.triple_whale_last_platform_click_nc_total_revenue`,
        `${queryPrefix}.triple_whale_full_first_click_nc_total_revenue`,
        `${queryPrefix}.triple_whale_full_last_click_nc_total_revenue`,
        `${queryPrefix}.triple_whale_linear_all_nc_total_revenue`,
        `${queryPrefix}.d30_brand_value`,
        `${queryPrefix}.d60_brand_value`,
        `${queryPrefix}.d90_brand_value`,
        `${queryPrefix}.d120_brand_value`,
        `${queryPrefix}.d150_brand_value`,
        `${queryPrefix}.estimated_d30_brand_value`,
        `${queryPrefix}.estimated_d60_brand_value`,
        `${queryPrefix}.estimated_d90_brand_value`,
        `${queryPrefix}.estimated_d120_brand_value`,
        `${queryPrefix}.estimated_d150_brand_value`,
        `${queryPrefix}.estimated_d180_brand_value`,
      ],
      dimensions: [
        `${queryPrefix}.campaign_id`,
        `${queryPrefix}.adset_id`,
        `${queryPrefix}.campaign_name`,
        `${queryPrefix}.adset_name`,
        `${queryPrefix}.media_channel`,
        `${queryPrefix}.objective`, // Add objective for model matching
        `${queryPrefix}.optimization_goal`,
        `${queryPrefix}.targeting_custom_audiences`,
        `${queryPrefix}.targeting_excluded_custom_audiences`,
      ],
      filters: [],
      timeDimensions: [
        {
          dimension: `${queryPrefix}.md_date`,
        },
      ],
    }

    // Apply last 30 days date range
    const finalQuery = polyfillCubeQueryWithTimeDimension(
      baseQuery as TCubeQuery,
      {
        dateRange: [startDate, endDate] as [Date, Date],
      },
    )

    return finalQuery
  }, [currentCompany?.id])

  const { resultSet: adsetResultSet, isLoading: isAdsetDataLoading } =
    useCubeQuery(adsetRevenueQuery)

  const adsetData = useMemo(() => {
    if (!adsetResultSet) return []
    return adsetResultSet.rawData() || []
  }, [adsetResultSet])

  // Map each campaign to its set of ad set IDs so an Adsets-tab initiative-type
  // change can be applied to the whole campaign by cascading to every sibling
  // ad set (see InitiativeTypeContainer.performInitiativeTypeChange). adsetData
  // is the unfiltered 30-day company-wide query, so the roster is complete.
  const adsetIdsByCampaign = useMemo(
    () => buildAdsetIdsByCampaign(adsetData as Array<Record<string, unknown>>),
    [adsetData],
  )

  useEffect(() => {
    void request()
  }, [activeWidget, currentCompany, request])

  // URL parameter effects for campaign and adset filtering
  useEffect(() => {
    const campaignIdFromUrl = params[URL_CAMPAIGN_ID_PARAM_KEY]
    if (campaignIdFromUrl) {
      const hasCampaignFilter = columnFilters.find(
        filter =>
          filter.id === URL_CAMPAIGN_ID_PARAM_KEY &&
          filter.operator === "contains" &&
          filter.value === campaignIdFromUrl,
      )
      if (hasCampaignFilter) {
        setParams({
          [URL_CAMPAIGN_ID_PARAM_KEY]: undefined,
        })
      } else {
        setColumnFilters((prevState: TColumnFiltersState | undefined) => {
          const newState = (
            prevState ? [...prevState] : []
          ) as TColumnFiltersState
          const filterIdx = newState.findIndex(
            filter =>
              filter.id === URL_CAMPAIGN_ID_PARAM_KEY &&
              filter.operator === "contains",
          )

          if (filterIdx >= 0) {
            newState[filterIdx].value = campaignIdFromUrl
          } else {
            newState.push({
              id: URL_CAMPAIGN_ID_PARAM_KEY,
              variant: "text",
              operator: "contains",
              value: campaignIdFromUrl,
            })
          }
          return newState
        })
      }
    }
  }, [params, columnFilters, setColumnFilters, setParams])

  useEffect(() => {
    const adsetIdFromUrl = params[URL_ADSET_ID_PARAM_KEY]
    if (adsetIdFromUrl) {
      const hasAdsetFilter = columnFilters.find(
        filter =>
          filter.id === URL_ADSET_ID_PARAM_KEY &&
          filter.operator === "contains" &&
          filter.value === adsetIdFromUrl,
      )
      if (hasAdsetFilter) {
        setParams({
          [URL_ADSET_ID_PARAM_KEY]: undefined,
        })
      } else {
        setColumnFilters((prevState: TColumnFiltersState | undefined) => {
          const newState = (
            prevState ? [...prevState] : []
          ) as TColumnFiltersState
          const filterIdx = newState.findIndex(
            filter =>
              filter.id === URL_ADSET_ID_PARAM_KEY &&
              filter.operator === "contains",
          )

          if (filterIdx >= 0) {
            newState[filterIdx].value = adsetIdFromUrl
          } else {
            newState.push({
              id: URL_ADSET_ID_PARAM_KEY,
              variant: "text",
              operator: "contains",
              value: adsetIdFromUrl,
            })
          }
          return newState
        })
      }
    }
  }, [params, columnFilters, setColumnFilters, setParams])

  const changePeriod = ({ start, end }: { start: Date; end: Date }) => {
    void setPeriod(
      dayjs(start).format(DATE_FORMAT),
      dayjs(end).format(DATE_FORMAT),
    )
  }

  const handleSetWidget = (widgetId: string) => {
    setParams({
      [URL_WIDGET_PARAM_KEY]: widgetId.toLowerCase(),
      [URL_CAMPAIGN_ID_PARAM_KEY]: undefined,
      [URL_ADSET_ID_PARAM_KEY]: undefined,
    })
    setRowSelection({})
  }

  // Create enhanced column config with custom component
  const columnConfigFiltered = useMemo(() => {
    const filtered = (chartProps?.columnConfig ?? []).filter(
      ({ key }: { key: string }) => (columnKeys as string[])?.includes(key),
    )

    // Find and enhance the initiative_type column with custom component
    return filtered.map((col: TableVisualizationColumn) => {
      if (
        col.key === "initiative_type" &&
        (col as TableVisualizationColumn & { cellType?: string }).cellType ===
          "custom-component"
      ) {
        return {
          ...col,
          cellType: "custom-component",
          customComponent: ({
            rowData,
            onStopPropagation,
          }: {
            rowData: Record<string, unknown>
            cellValue: unknown
            onStopPropagation: (e: React.MouseEvent) => void
          }) => {
            const currentValue = getCurrentInitiativeType(rowData)
            const options = getInitiativeTypeOptions(rowData)
            const childAdsetIds = getRowChildAdsetIds(
              rowData,
              adsetIdsByCampaign,
            )
            return (
              <InitiativeTypeDropdown
                value={currentValue}
                options={options}
                onChange={value =>
                  handleInitiativeTypeChange(value, {
                    ...rowData,
                    childAdsetIds,
                  })
                }
                onStopPropagation={onStopPropagation}
              />
            )
          },
        }
      }
      return col
    })
  }, [
    chartProps?.columnConfig,
    columnKeys,
    getCurrentInitiativeType,
    getInitiativeTypeOptions,
    handleInitiativeTypeChange,
    adsetIdsByCampaign,
  ])

  const isLoading =
    activeWidget?.id !== VISUALIZATION_TABLE.CUSTOM_ATTRIBUTION &&
    activeWidget?.id !== VISUALIZATION_TABLE.REVENUE_BREAKDOWN &&
    isMediaChannelsLoading

  const shouldShowControls =
    activeWidget?.id !== VISUALIZATION_TABLE.CUSTOM_ATTRIBUTION &&
    activeWidget?.id !== VISUALIZATION_TABLE.REVENUE_BREAKDOWN

  return (
    <>
      <Breadcrumbs
        items={[
          {
            slug: parent.slug,
            name: parent.title,
          },
          {
            slug,
            name: title,
          },
        ]}
      />

      <TabContainer
        visibleWidgets={visibleWidgets}
        onTabChange={handleSetWidget}
      />

      {shouldShowControls && (
        <ControlsContainer
          isLoading={isLoading}
          sourcesOptions={sourcesOptions}
          sourceKeys={sourceKeys}
          setSourceKeys={setSourceKeys}
          highlight={highlight}
          setHighlight={setHighlight}
          amazonEnabled={amazonEnabled}
          toggleAmazon={toggleAmazon}
          columnOptions={columnOptions}
          columnKeys={columnKeys as string[]}
          setColumnKeys={setColumnKeys}
          dateRange={dateRange}
          lastDateRestriction={lastDateRestriction}
          onDateChange={changePeriod}
          chartProps={chartProps}
          columnConfigFiltered={columnConfigFiltered}
          brandMediaId={BRAND_MEDIA_ID}
          lastDate={lastDate}
          isLastUpdateDateLoading={isLastUpdateDateLoading}
          onAutoClassify={() => autoClassify(adsetData)}
          isClassifying={isClassifying}
          hasUnassignedAdsets={hasUnassignedAdsets(adsetData)}
        />
      )}

      <ContentContainer
        activeWidget={activeWidget}
        isLoading={isLoading}
        highlight={highlight}
        dateRange={dateRange}
        items={items}
        cubeFilters={cubeFilters}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        sources={sources}
        queryPrefix={queryPrefix}
        chartPropsSorting={chartPropsSorting}
        defaultColumnKeys={defaultColumnKeys}
        columnConfigFiltered={columnConfigFiltered}
        chartProps={chartProps}
        brandMediaId={BRAND_MEDIA_ID}
        sourceKeys={sourceKeys}
        adsetData={adsetData}
        isAdsetDataLoading={isAdsetDataLoading}
      />
    </>
  )
}

export default function PaidMediaWeightingDashboard(
  props: TBrandMediaDashboardProps,
) {
  return (
    <InitiativeTypeContainer>
      <PaidMediaWeightingContent {...props} />
    </InitiativeTypeContainer>
  )
}
